<template>
	<component v-if="icon" :is="tagName" :class="['svg-icon', color]" />
</template>

<script setup>
import { computed, defineAsyncComponent } from "vue";

const props = defineProps({
	icon: { type: String, required: true },
	color: { type: String, required: false, default: undefined },
});

const tagName = computed(() => {
	if (!props.icon) return null;
	switch (props.icon.toLowerCase()) {
		case "accessible":
			return defineAsyncComponent(() => import("~/svg/IconAccessible.vue"));
		case "alert":
			return defineAsyncComponent(() => import("~/svg/IconAlert.vue"));
		case "arrowback":
			return defineAsyncComponent(() => import("~/svg/IconArrowBack.vue"));
		case "arrowdown":
			return defineAsyncComponent(() => import("~/svg/IconArrowDown.vue"));
		case "arrowright":
			return defineAsyncComponent(() => import("~/svg/IconArrowRight.vue"));
		case "arrowupright":
			return defineAsyncComponent(() => import("~/svg/IconArrowUpRight.vue"));
		case "asterisk":
			return defineAsyncComponent(() => import("~/svg/IconAsterisk.vue"));
		case "back":
			return defineAsyncComponent(() => import("~/svg/IconBack.vue"));
		case "bag":
			return defineAsyncComponent(() => import("~/svg/IconBag.vue"));
		case "bigtop":
			return defineAsyncComponent(() => import("~/svg/IconBigTop.vue"));
		case "bulletlist":
			return defineAsyncComponent(() => import("~/svg/IconBulletList.vue"));
		case "camera":
			return defineAsyncComponent(() => import("~/svg/IconCamera.vue"));
		case "card":
			return defineAsyncComponent(() => import("~/svg/IconCard.vue"));
		case "checkmark":
			return defineAsyncComponent(() => import("~/svg/IconCheckmark.vue"));
		case "chevron":
			return defineAsyncComponent(() => import("~/svg/IconChevron.vue"));
		case "clapperboard":
			return defineAsyncComponent(() => import("~/svg/IconClapperBoard.vue"));
		case "clock":
			return defineAsyncComponent(() => import("~/svg/IconClock.vue"));
		case "crown":
			return defineAsyncComponent(() => import("~/svg/IconCrown.vue"));
		case "curtain":
			return defineAsyncComponent(() => import("~/svg/IconCurtain.vue"));
		case "dot":
			return defineAsyncComponent(() => import("~/svg/IconDot.vue"));
		case "externallink":
			return defineAsyncComponent(() => import("~/svg/IconExternalLink.vue"));
		case "facebook":
			return defineAsyncComponent(() => import("~/svg/IconFacebook.vue"));
		case "family":
			return defineAsyncComponent(() => import("~/svg/IconFamily.vue"));
		case "foodandbeverage":
			return defineAsyncComponent(() =>
				import("~/svg/IconFoodAndBeverage.vue")
			);
		case "gift":
			return defineAsyncComponent(() => import("~/svg/IconGift.vue"));
		case "home":
			return defineAsyncComponent(() => import("~/svg/IconHome.vue"));
		case "info":
			return defineAsyncComponent(() => import("~/svg/IconInfo.vue"));
		case "logout":
			return defineAsyncComponent(() => import("~/svg/IconLogOut.vue"));
		case "map":
			return defineAsyncComponent(() => import("~/svg/IconMap.vue"));
		case "masquerade":
			return defineAsyncComponent(() => import("~/svg/IconMasquerade.vue"));
		case "minus":
			return defineAsyncComponent(() => import("~/svg/IconMinus.vue"));
		case "nofood":
			return defineAsyncComponent(() => import("~/svg/IconNoFood.vue"));
		case "parking":
			return defineAsyncComponent(() => import("~/svg/IconParking.vue"));
		case "pause":
			return defineAsyncComponent(() => import("~/svg/IconPause.vue"));
		case "pin":
			return defineAsyncComponent(() => import("~/svg/IconPin.vue"));
		case "play":
			return defineAsyncComponent(() => import("~/svg/IconPlay.vue"));
		case "plus":
			return defineAsyncComponent(() => import("~/svg/IconPlus.vue"));
		case "profile":
			return defineAsyncComponent(() => import("~/svg/IconProfile.vue"));
		case "profilefilled":
			return defineAsyncComponent(() => import("~/svg/IconProfileFilled.vue"));
		case "program":
			return defineAsyncComponent(() => import("~/svg/IconProgram.vue"));
		case "seat":
			return defineAsyncComponent(() => import("~/svg/IconSeat.vue"));
		case "snowflake":
			return defineAsyncComponent(() => import("~/svg/IconSnowflake.vue"));
		case "soundoff":
			return defineAsyncComponent(() => import("~/svg/IconSoundOff.vue"));
		case "soundon":
			return defineAsyncComponent(() => import("~/svg/IconSoundOn.vue"));
		case "ticket":
			return defineAsyncComponent(() => import("~/svg/IconTicket.vue"));
		case "x":
			return defineAsyncComponent(() => import("~/svg/IconX.vue"));
		case "instagram":
			return defineAsyncComponent(() => import("~/svg/IconInstagram.vue"));
		case "navhome":
			return defineAsyncComponent(() => import("~/svg/IconNavHome.vue"));
		case "navplan":
			return defineAsyncComponent(() => import("~/svg/IconNavPlan.vue"));
		case "navprogram":
			return defineAsyncComponent(() => import("~/svg/IconNavProgram.vue"));
		case "navshop":
			return defineAsyncComponent(() => import("~/svg/IconNavShop.vue"));
		case "navticket":
			return defineAsyncComponent(() => import("~/svg/IconNavTicket.vue"));
		default:
			return null;
	}
});
</script>

<style lang="css">
.svg-icon {
	path,
	&.default path,
	&.primary path {
		fill: var(--primary-color, #000);
	}

	&.secondary path {
		fill: var(--secondary-color, #000);
	}

	&.tertiary path {
		fill: var(--tertiary-color, #000);
	}

	&.white path {
		fill: var(--color-white, #fff);
	}

	&.black path {
		fill: var(--color-black, #000);
	}

	&.orange path {
		fill: var(--color-orange, #fd470b);
	}

	&.green path {
		fill: var(--accent-color, #2eac83);
	}

	&.gold path {
		fill: var(--color-gold, #8d723e);
	}

	&.grey path {
		fill: var(--color-grey, #282828);
	}

	&.light-grey path {
		fill: var(--color-light-grey, #767676);
	}
}
</style>
